import { useState } from 'react';
import { Price, Select, Switch } from '@artosera/ui';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useMettreEnVente, useOeuvresBoutique } from '../../api/boutique.js';
import { galerieALaction } from '../../auth/sujet.js';
import { ObjetCartel } from '../../composants/ObjetCartel.js';
import { PastilleStatut } from '../../composants/PastilleStatut.js';
import { useLangue } from '../../composants/useLangue.js';
import { useShellContext } from '../shell/context.js';
import { ErreurBoutique, EtatVide, PaginationBoutique, VignetteBoutique } from './commun.js';
import styles from './BoutiquePage.module.css';
export function OeuvresEnVenteTab() {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const langue = useLangue();
  const [q, setQ] = useState('');
  const [vente, setVente] = useState<'' | 'true' | 'false'>('');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const liste = useOeuvresBoutique(galerieSlug, {
    q,
    page,
    ...(vente ? { enVente: vente === 'true' } : {}),
  });
  const mutation = useMettreEnVente(galerieSlug);
  const peutEcrire = galerieALaction(galerie, 'shop:write');
  return (
    <div className={styles.contenu}>
      <p className={styles.secondaire}>{t('regie:boutique.oeuvresAide')}</p>
      <div className={styles.filtres}>
        <label className={styles.recherche}>
          <span>{t('regie:boutique.rechercher')}</span>
          <input
            type="search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </label>
        <label className={styles.filtre}>
          <span>{t('regie:boutique.filtreVente')}</span>
          <Select
            aria-label={t('regie:boutique.filtreVente')}
            value={vente}
            placeholder={t('regie:boutique.toutesOeuvres')}
            onValueChange={(v) => {
              setVente(v as '' | 'true' | 'false');
              setPage(1);
            }}
            options={[
              { value: '', label: t('regie:boutique.toutesOeuvres') },
              { value: 'true', label: t('regie:boutique.seulementEnVente') },
              { value: 'false', label: t('regie:boutique.seulementHorsVente') },
            ]}
          />
        </label>
        <span className={styles.secondaire}>
          {liste.data
            ? t('regie:boutique.totalOeuvres', { count: liste.data.pagination.total })
            : ''}
        </span>
      </div>
      {mutation.isError && <ErreurBoutique erreur={mutation.error} />}
      {liste.isLoading ? (
        <p role="status">{t('commun:chargement')}</p>
      ) : liste.isError ? (
        <ErreurBoutique erreur={liste.error} reessayer={() => void liste.refetch()} />
      ) : !liste.data?.oeuvres.length ? (
        q || vente ? (
          <p className={styles.vide}>{t('regie:boutique.aucuneOeuvre')}</p>
        ) : (
          <EtatVide
            titre={t('regie:boutique.catalogueVide.titre')}
            aide={t('regie:boutique.catalogueVide.aide')}
            action={{
              libelle: t('regie:boutique.catalogueVide.action'),
              onClick: () => navigate(`/${galerieSlug}/oeuvres`),
            }}
          />
        )
      ) : (
        <>
          <table className={styles.tableau} data-density="registre">
            <thead>
              <tr>
                {['oeuvre', 'statutOeuvre', 'prixTtc', 'enVente'].map((k) => (
                  <th scope="col" key={k}>
                    {t(`regie:boutique.${k}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {liste.data.oeuvres.map((o) => (
                <tr key={o.id}>
                  <td>
                    <Link className={styles.cartelLien} to={`/${galerieSlug}/oeuvres/${o.id}`}>
                      <VignetteBoutique
                        galerie={galerieSlug}
                        mediaId={o.mediaPrincipalId}
                        url={o.apercuUrl}
                      />
                      <ObjetCartel titre={o.titre} artiste={o.artisteNom} />
                    </Link>
                  </td>
                  <td data-libelle={t('regie:boutique.statutOeuvre')}>
                    <PastilleStatut statut={o.statut} />
                  </td>
                  <td data-libelle={t('regie:boutique.prixTtc')}>
                    {o.raison ? (
                      <span className={styles.secondaire}>
                        {t(`regie:boutique.raisons.${o.raison}`)}
                      </span>
                    ) : o.prixTtcCentimes !== null ? (
                      <Price cents={o.prixTtcCentimes} lang={langue} />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <Switch
                      id={`vente-${o.id}`}
                      label={t('regie:boutique.enVente')}
                      checked={o.enVente}
                      disabled={!peutEcrire || !!o.raison || mutation.isPending}
                      onCheckedChange={(enVente) => mutation.mutate({ id: o.id, enVente })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <PaginationBoutique pagination={liste.data.pagination} changer={setPage} />
        </>
      )}
    </div>
  );
}
